import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RotateCcw } from "lucide-react";
import type { ManagedUser } from "./types";
import { apiErrorMessage, reactivateTrial } from "./trials-api";
import { TrialConfirmDialog } from "./TrialConfirmDialog";
import { Chip, Stepper } from "./trial-stepper";

const PRESET_DAYS = [7, 14, 21, 30] as const;
const DEFAULT_DAYS = 14;
const MIN_DAYS = 1;
const MAX_DAYS = 365;

type Props = {
  user: ManagedUser | null;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

export const ReactivateTrialDialog = ({
  user,
  open,
  onClose,
  onSuccess,
}: Props) => {
  const [lengthDays, setLengthDays] = useState<number>(DEFAULT_DAYS);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setLengthDays(DEFAULT_DAYS);
      setSubmitting(false);
    }
  }, [open]);

  if (!user) return null;

  const numericId = parseInt(user.id, 10);
  const valid = !isNaN(numericId) && lengthDays >= MIN_DAYS && lengthDays <= MAX_DAYS;

  const handleConfirm = async () => {
    if (!valid) return;
    setSubmitting(true);
    try {
      await reactivateTrial(numericId, lengthDays);
      toast.success(`Trial reactivated · ${lengthDays} days`);
      onSuccess();
      onClose();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to reactivate trial"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <TrialConfirmDialog
      open={open}
      onClose={onClose}
      onConfirm={handleConfirm}
      submitting={submitting}
      canConfirm={valid}
      icon={RotateCcw}
      title="Reactivate trial?"
      description={`Restore trial access for ${user.name || user.email}.`}
      confirmLabel="Confirm reactivation"
      submittingLabel="Reactivating…"
      helperText="The teacher will regain access immediately after reactivation."
    >
      <div className="rounded-lg border border-border/60 bg-muted/20 p-4 space-y-3">
        <h3 className="text-sm font-semibold">Trial length</h3>
        <div className="flex flex-wrap items-center gap-3">
          <Stepper
            value={lengthDays}
            min={MIN_DAYS}
            max={MAX_DAYS}
            onChange={setLengthDays}
            disabled={submitting}
          />
          <span className="text-sm text-muted-foreground">days</span>
          <div className="flex flex-wrap gap-2">
            {PRESET_DAYS.map((d) => (
              <Chip
                key={d}
                active={lengthDays === d}
                onClick={() => setLengthDays(d)}
                disabled={submitting}
              >
                {d}
              </Chip>
            ))}
          </div>
        </div>
      </div>
    </TrialConfirmDialog>
  );
};
